import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
    createReturnRequest,
    sendReturnMessage,
    uploadReturnEvidence,
    submitReturn,
    addLocalReturnMessage,
    setReturnId,
    clearReturnChat,
    openReturnConfirmModal,
    closeReturnConfirmModal,
    processSequentialStep,
    evaluateReturn,
    pushBotMessage,
    setSelectedSolution,
    setSelectedRefundMethod,
    updateCustomerAddress,
    setFlowStep,
    setSupportRequested,
    setReplacementProductId,
    setIsLoading,
} from '../store/slices/returnChatSlice';
import { ChatMessage } from '@/types';

export const useReturnChat = () => {
    const dispatch = useDispatch<AppDispatch>();
    const returnChatState = useSelector((state: RootState) => state.returnChat);
    const [message, setMessage] = useState('');

    /**
     * Start a return request for an order.
     */
    const handleCreateReturnRequest = useCallback(async (args: {
        order_id: string;
        product_id: string;
    }) => {
        try {
            const result = await dispatch(createReturnRequest(args)).unwrap();
            return result;
        } catch (error) {
            console.error('Failed to create return request:', error);
            throw error;
        }
    }, [dispatch]);

    /**
     * Send a chat message to the return AI.
     */
    const handleSendReturnMessage = useCallback(async (msg: ChatMessage) => {
        if (!returnChatState.returnId) return;

        dispatch(addLocalReturnMessage(msg));
        setMessage('');

        return await dispatch(sendReturnMessage({ message: msg })).unwrap();
    }, [dispatch, returnChatState.returnId]);

    /**
     * Upload evidence files for the return.
     */
    const handleUploadReturnEvidence = useCallback(async (args: { formData: FormData, helperText?: string }) => {
        if (!returnChatState.returnId) return;
        await dispatch(uploadReturnEvidence(args)).unwrap();
    }, [dispatch, returnChatState.returnId]);

    /**
     * Submit the return request (marks as submitted).
     */
    const handleSubmitReturn = useCallback(async () => {
        if (!returnChatState.returnId) return;
        await dispatch(submitReturn());
    }, [dispatch, returnChatState.returnId]);

    const handleSetReturnId = useCallback((id: string) => {
        dispatch(setReturnId(id));
    }, [dispatch]);

    const handleClearReturnChat = useCallback(() => {
        dispatch(clearReturnChat());
    }, [dispatch]);

    const handleOpenConfirmModal = useCallback(() => {
        dispatch(openReturnConfirmModal());
    }, [dispatch]);

    const handleCloseConfirmModal = useCallback(() => {
        dispatch(closeReturnConfirmModal());
    }, [dispatch]);

    return {
        ...returnChatState,
        message,
        setMessage,
        handleCreateReturnRequest,
        handleSendReturnMessage,
        handleUploadReturnEvidence,
        handleSubmitReturn,
        handleSetReturnId,
        handleClearReturnChat,
        handleOpenConfirmModal,
        handleCloseConfirmModal,
        handleProcessSequentialStep: useCallback(() => dispatch(processSequentialStep()), [dispatch]),
        handleSetIsLoading: useCallback((loading: boolean) => dispatch(setIsLoading(loading)), [dispatch]),
        handleEvaluateReturn: useCallback(() => {
            if (returnChatState.returnId) {
                dispatch(evaluateReturn(returnChatState.returnId));
            }
        }, [dispatch, returnChatState.returnId]),
        handleSetSelectedSolution: useCallback((sol: string) => dispatch(setSelectedSolution(sol)), [dispatch]),
        handleUpdateCustomerAddress: useCallback((addr: any) => dispatch(updateCustomerAddress(addr)), [dispatch]),
        handlePushBotMessage: useCallback((payload: any) => dispatch(pushBotMessage(payload)), [dispatch]),
        handleSetFlowStep: useCallback((step: number) => dispatch(setFlowStep(step)), [dispatch]),
        handleSetReplacementProductId: useCallback((id: string) => dispatch(setReplacementProductId(id)), [dispatch]),
        handleSetSelectedRefundMethod: useCallback((method: string) => dispatch(setSelectedRefundMethod(method)), [dispatch]),
        handleSetSupportRequested: useCallback((val: boolean) => dispatch(setSupportRequested(val)), [dispatch]),
    };
};
